import { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import PropTypes from "prop-types";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CampaignIcon from "@mui/icons-material/Campaign";
import DatasetIcon from "@mui/icons-material/Dataset";
import ListIcon from "@mui/icons-material/List";
import MarkEmailUnreadIcon from "@mui/icons-material/MarkEmailUnread";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import RefreshIcon from "@mui/icons-material/Refresh";
import WorkIcon from "@mui/icons-material/Work";
import YouTubeIcon from "@mui/icons-material/YouTube";
import PageHeader from "../components/PageHeader";
import SurfaceCard from "../components/SurfaceCard";
import {
  getCampaings,
  getEnquiries,
  getJobPlacements,
  getResults,
  getSchoolTeachers,
  getTeachers,
  getTutors,
  getVideos,
} from "firestoreService";

// `regDate` and `createdAt` are both stored as strings that start with
// YYYY-MM-DD, so a plain string comparison is enough to count a window and we
// avoid parsing dates that were never validated as dates.
const daysAgoStamp = (days) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().split("T")[0];
};

const countSince = (rows, field, stamp) =>
  rows.filter((row) => typeof row[field] === "string" && row[field] >= stamp).length;

function StatTile({ label, value, hint, icon: Icon, tone, to, loading }) {
  return (
    <SurfaceCard sx={{ height: "100%" }}>
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Avatar variant="rounded" sx={{ bgcolor: tone, width: 44, height: 44 }}>
          <Icon fontSize="small" sx={{ color: "#fff" }} />
        </Avatar>
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography sx={{ fontSize: "0.78rem", color: "#7b809a", fontWeight: 600 }}>
            {label}
          </Typography>
          {loading ? (
            <Skeleton width={56} height={38} />
          ) : (
            <Typography
              sx={{ fontSize: "1.75rem", fontWeight: 700, color: "#1b2a41", lineHeight: 1.2 }}
            >
              {value}
            </Typography>
          )}
          {hint && !loading && (
            <Typography sx={{ fontSize: "0.75rem", color: "#7b809a" }}>{hint}</Typography>
          )}
        </Box>
      </Stack>
      {to && (
        <Button
          component={RouterLink}
          to={to}
          size="small"
          endIcon={<ArrowForwardIcon />}
          sx={{ mt: 1.5, textTransform: "none", px: 0, fontSize: "0.78rem" }}
        >
          Open
        </Button>
      )}
    </SurfaceCard>
  );
}

StatTile.defaultProps = { hint: "", to: "", loading: false };

StatTile.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  hint: PropTypes.string,
  icon: PropTypes.elementType.isRequired,
  tone: PropTypes.string.isRequired,
  to: PropTypes.string,
  loading: PropTypes.bool,
};

function RecentList({ title, rows, emptyText, to, nameField, metaField, loading }) {
  return (
    <SurfaceCard sx={{ height: "100%" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#1b2a41" }}>
          {title}
        </Typography>
        <Button component={RouterLink} to={to} size="small" sx={{ textTransform: "none" }}>
          View all
        </Button>
      </Stack>
      <Divider sx={{ mb: 1 }} />

      {loading && [0, 1, 2, 3, 4].map((key) => <Skeleton key={key} height={44} />)}

      {!loading && rows.length === 0 && (
        <Typography sx={{ py: 3, textAlign: "center", color: "#7b809a", fontSize: "0.85rem" }}>
          {emptyText}
        </Typography>
      )}

      {!loading &&
        rows.map((row) => (
          <Stack
            key={row.id}
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ py: 1, borderBottom: "1px solid #f0f2f5", "&:last-of-type": { borderBottom: 0 } }}
          >
            <Avatar
              src={row.photo}
              sx={{ width: 32, height: 32, bgcolor: "#174276", fontSize: "0.8rem" }}
            >
              {(row[nameField] ?? "?")[0]?.toUpperCase()}
            </Avatar>
            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
              <Typography noWrap sx={{ fontSize: "0.875rem", fontWeight: 600, color: "#1b2a41" }}>
                {row[nameField] || "Unnamed"}
              </Typography>
              <Typography noWrap sx={{ fontSize: "0.75rem", color: "#7b809a" }}>
                {row.mobileNo || row.email || "No contact details"}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: "0.72rem", color: "#7b809a", whiteSpace: "nowrap" }}>
              {(row[metaField] ?? "").slice(0, 10)}
            </Typography>
          </Stack>
        ))}
    </SurfaceCard>
  );
}

RecentList.defaultProps = { loading: false };

RecentList.propTypes = {
  title: PropTypes.string.isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  emptyText: PropTypes.string.isRequired,
  to: PropTypes.string.isRequired,
  nameField: PropTypes.string.isRequired,
  metaField: PropTypes.string.isRequired,
  loading: PropTypes.bool,
};

function Dashboard() {
  const [state, setState] = useState({ loading: true, error: "", data: null });

  const load = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: "" }));
    try {
      // Every panel already fetched its own collection on mount; the dashboard
      // fetches them together so the counts are from one moment in time.
      const [tutors, jobs, enquiries, teachers, schoolTeachers, results, campaigns, videos] =
        await Promise.all([
          getTutors(),
          getJobPlacements(),
          getEnquiries(),
          getTeachers(),
          getSchoolTeachers(),
          getResults(),
          getCampaings(),
          getVideos(),
        ]);

      setState({
        loading: false,
        error: "",
        data: {
          tutors,
          jobs,
          enquiries,
          teachers,
          schoolTeachers,
          results: results?.data ?? [],
          campaigns: campaigns?.data ?? [],
          videos: videos?.data ?? [],
        },
      });
    } catch (error) {
      console.error("Could not load dashboard:", error);
      setState({
        loading: false,
        error: "Could not load the dashboard. Check your connection and try again.",
        data: null,
      });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const { loading, error, data } = state;
  const weekStamp = useMemo(() => daysAgoStamp(7), []);

  const stats = useMemo(() => {
    if (!data) return null;
    return {
      tutorsTotal: data.tutors.length,
      tutorsWeek: countSince(data.tutors, "regDate", weekStamp),
      jobsTotal: data.jobs.length,
      jobsWeek: countSince(data.jobs, "regDate", weekStamp),
      enquiriesNew: data.enquiries.filter((row) => row.status === "new").length,
      enquiriesTotal: data.enquiries.length,
      teachersTotal: data.teachers.length + data.schoolTeachers.length,
      resultsTotal: data.results.length,
      campaignsTotal: data.campaigns.length,
      videosTotal: data.videos.length,
    };
  }, [data, weekStamp]);

  return (
    <>
      <PageHeader
        title="Dashboard"
        description="Everything submitted through the website, and everything published on it."
        actions={
          <Button
            onClick={load}
            startIcon={<RefreshIcon />}
            variant="outlined"
            sx={{ textTransform: "none" }}
            disabled={loading}
          >
            Refresh
          </Button>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} action={<Button onClick={load}>Retry</Button>}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2.5}>
        <Grid item xs={12} sm={6} lg={3}>
          <StatTile
            label="Tutor applications"
            value={stats?.tutorsTotal ?? 0}
            hint={`${stats?.tutorsWeek ?? 0} in the last 7 days`}
            icon={ListIcon}
            tone="#174276"
            to="/admin/tutors"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatTile
            label="Job placements"
            value={stats?.jobsTotal ?? 0}
            hint={`${stats?.jobsWeek ?? 0} in the last 7 days`}
            icon={WorkIcon}
            tone="#1b6fd1"
            to="/admin/jobs"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatTile
            label="Unread enquiries"
            value={stats?.enquiriesNew ?? 0}
            hint={`${stats?.enquiriesTotal ?? 0} in total`}
            icon={MarkEmailUnreadIcon}
            tone="#f5a524"
            to="/admin/enquiries"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatTile
            label="Published teachers"
            value={stats?.teachersTotal ?? 0}
            hint="Institute and play school"
            icon={PeopleAltIcon}
            tone="#4caf50"
            to="/admin/teachers"
            loading={loading}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
        <Grid item xs={12} md={6}>
          <RecentList
            title="Latest tutor applications"
            rows={(data?.tutors ?? []).slice(0, 5)}
            emptyText="No tutor applications yet."
            to="/admin/tutors"
            nameField="name"
            metaField="regDate"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <RecentList
            title="Latest job placements"
            rows={(data?.jobs ?? []).slice(0, 5)}
            emptyText="No job placement applications yet."
            to="/admin/jobs"
            nameField="name"
            metaField="regDate"
            loading={loading}
          />
        </Grid>
      </Grid>

      <SurfaceCard sx={{ mt: 2.5 }}>
        <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#1b2a41", mb: 1.5 }}>
          Published on the public site
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip
            icon={<DatasetIcon />}
            label={`${stats?.resultsTotal ?? 0} result images`}
            component={RouterLink}
            to="/admin/results"
            clickable
          />
          <Chip
            icon={<CampaignIcon />}
            label={`${stats?.campaignsTotal ?? 0} campaign images`}
            component={RouterLink}
            to="/admin/campaigns"
            clickable
          />
          <Chip
            icon={<YouTubeIcon />}
            label={`${stats?.videosTotal ?? 0} videos`}
            component={RouterLink}
            to="/admin/youtube"
            clickable
          />
        </Stack>
      </SurfaceCard>
    </>
  );
}

export default Dashboard;
